import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, Chip, Grid2 as Grid, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  reactivateLicence,
  readTenant,
  resendOwnerInvitation,
  resetOwnerPassword,
  saveLicence,
} from '../../api/admin/endpoints';
import type { LicenceInput } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatBytes, formatDate, formatDateTime } from '../../components/format';
import { LicenceFields } from '../../components/LicenceFields';
import { licenceDraftFrom, licenceInputFrom, type LicenceDraft } from '../../components/licenceDraft';
import { LicenceStateChip } from '../../components/LicenceStateChip';
import { PageHeader } from '../../components/PageHeader';
import { PasswordResetDialog } from '../../components/PasswordResetDialog';
import { StatusChip } from '../../components/StatusChip';
import { UsageMeters } from '../../components/UsageMeters';
import { useLanguage } from '../../i18n/LanguageContext';

import { EditTenantDialog } from './EditTenantDialog';
import { OrderLicenceDialog } from './OrderLicenceDialog';
import { SuspendLicenceDialog } from './SuspendLicenceDialog';

const INVALIDATES: Array<string> = ['tenant', 'tenants', 'audit'];

/** One business: its licence and usage, its owner (US-02, US-03, US-04). */
export const TenantDetailPage = () => {
  const { tenantId = '' } = useParams();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['tenant', tenantId], queryFn: () => readTenant(tenantId) });
  const tenant = query.data;
  const [draft, setDraft] = useState<LicenceDraft | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [dialog, setDialog] = useState<'edit' | 'suspend' | 'password' | 'order' | null>(null);

  useEffect(() => {
    if (tenant) {
      setDraft(licenceDraftFrom(tenant.licence));
    }
  }, [tenant]);

  const save = useAppMutation(
    (input: LicenceInput) => saveLicence(tenantId, input),
    INVALIDATES,
    () => setSaved(true),
  );
  const reactivate = useAppMutation(() => reactivateLicence(tenantId), INVALIDATES);
  const invite = useAppMutation(() => resendOwnerInvitation(tenantId), INVALIDATES);

  if (query.isLoading) {
    return <Skeleton variant="rounded" height={320} />;
  }

  if (!tenant || !draft) {
    return (
      <>
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/platform/tenants')}>
          {t('tenants.back')}
        </Button>
      </>
    );
  }

  const submitLicence = (): void => {
    const input = licenceInputFrom(draft);

    setSaved(false);

    if (input === null) {
      setProblem(t('licence.invalid'));

      return;
    }

    setProblem(null);
    save.mutate(input);
  };

  return (
    <>
      <PageHeader
        title={tenant.name}
        subtitle={tenant.contactEmail ?? undefined}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button onClick={() => setDialog('edit')}>{t('tenants.edit')}</Button>
            <Button
              component={RouterLink}
              to={`/platform/invoices?tenantId=${tenant.id}&tenantName=${encodeURIComponent(tenant.name)}`}
            >
              {t('tenants.invoices')}
            </Button>
            <Button variant="contained" onClick={() => setDialog('order')}>
              {t('licence.order')}
            </Button>
            {tenant.licence.status === 'PENDING_PAYMENT' ? null : tenant.licence.status === 'ACTIVE' ? (
              <Button color="error" variant="outlined" onClick={() => setDialog('suspend')}>
                {t('licence.suspend')}
              </Button>
            ) : (
              <Button
                variant="outlined"
                disabled={reactivate.isPending}
                onClick={() => reactivate.mutate(undefined)}
              >
                {t('licence.reactivate')}
              </Button>
            )}
          </Stack>
        }
      />
      <ErrorNotice error={reactivate.error} />
      {tenant.licence.status === 'PENDING_PAYMENT' ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          {t('licence.awaitingHint')}
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Typography variant="h6">{t('tenants.usage')}</Typography>
              <LicenceStateChip state={tenant.licence.state} />
            </Stack>
            <UsageMeters usage={tenant.usage} limits={tenant.licence.limits} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('tenants.imageStorage', { size: formatBytes(tenant.imageBytes, language) })}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('tenants.licence')}
            </Typography>
            <Stack spacing={2}>
              {problem ? (
                <Alert severity="warning" variant="outlined">
                  {problem}
                </Alert>
              ) : null}
              {saved ? (
                <Alert severity="success" variant="outlined">
                  {t('licence.saved')}
                </Alert>
              ) : null}
              <ErrorNotice error={save.error} />
              <LicenceFields draft={draft} onChange={setDraft} showNotes />
              <Typography variant="caption" color="text.secondary">
                {t('licence.lowerHint')}
              </Typography>
              <Box>
                <Button variant="contained" disabled={save.isPending} onClick={submitLicence}>
                  {t('licence.save')}
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('tenants.owner')}
            </Typography>
            <Stack spacing={1.25}>
              <Typography variant="body1" sx={{ fontWeight: 560 }}>
                {tenant.owner.displayName ?? tenant.owner.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tenant.owner.email}
              </Typography>
              <Stack direction="row" spacing={0.75}>
                <StatusChip status={tenant.owner.status} />
                {tenant.owner.invitationPending ? (
                  <Chip size="small" color="info" variant="outlined" label={t('invitation.pending')} />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('field.lastLoginAt')}: {formatDateTime(tenant.owner.lastLoginAt, language)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('field.createdAt')}: {formatDate(tenant.createdAt, language)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tenants.billingCompany')}:{' '}
                {tenant.billingCompany
                  ? `${tenant.billingCompany.name}${tenant.billingCompany.billable ? '' : ` (${t('tenants.notBillable')})`}`
                  : t('tenants.noBillingCompany')}
              </Typography>
              <ErrorNotice error={invite.error} />
              <Stack direction="row" spacing={1} sx={{ pt: 1 }} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={() => setDialog('password')}>
                  {t('password.reset')}
                </Button>
                {tenant.owner.invitationPending ? (
                  <Button
                    variant="outlined"
                    disabled={invite.isPending || invite.isSuccess}
                    onClick={() => invite.mutate(undefined)}
                  >
                    {invite.isSuccess ? t('invitation.sent') : t('invitation.resend')}
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {dialog === 'edit' ? <EditTenantDialog tenant={tenant} onClose={() => setDialog(null)} /> : null}
      {dialog === 'suspend' ? (
        <SuspendLicenceDialog tenantId={tenant.id} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'order' ? <OrderLicenceDialog tenant={tenant} onClose={() => setDialog(null)} /> : null}
      {dialog === 'password' ? (
        <PasswordResetDialog
          subject={tenant.owner.email}
          onSubmit={(password) => resetOwnerPassword(tenant.id, password)}
          invalidates={INVALIDATES}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
};
